import React, { useState, useEffect, useRef } from "react";
import "./FingerprintCapture.css";

const FingerprintCapture = ({
  setFingerBlob,
  setFingerDataURL,
  fingerPrint,
}) => {
  const [status, setStatus] = useState("Initializing...");
  const [imageData, setImageData] = useState(null);
  const [quality, setQuality] = useState("");
  const [readers, setReaders] = useState([]);
  const [selectedReader, setSelectedReader] = useState("");
  const [captureStarted, setCaptureStarted] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [sdkInitialized, setSdkInitialized] = useState(false);
  const [deviceConnected, setDeviceConnected] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const currentFormat = "PngImage";
  const sdkRef = useRef(null);

  // Initialize the SDK when component mounts
  useEffect(() => {
    if (!window.Fingerprint) {
      const script1 = document.createElement("script");
      script1.src = "/scripts/fingerprint.sdk.min.js";
      script1.onload = () => {
        const script2 = document.createElement("script");
        script2.src = "/scripts/websdk.client.bundle.min.js";
        script2.onload = () => {
          initializeSDK();
          setSdkInitialized(true);
        };
        document.body.appendChild(script2);
      };
      document.body.appendChild(script1);
    } else {
      initializeSDK();
      setSdkInitialized(true);
    }

    return () => {
      if (sdkRef.current && sdkRef.current.acquisitionStarted) {
        stopCapture();
      }
    };
  }, []);

  // Handle initial fingerprint prop and changes
  useEffect(() => {
    if (isInitialLoad && fingerPrint) {
      setImageData(fingerPrint);
      setIsInitialLoad(false);
    }
  }, [fingerPrint, isInitialLoad]);

  // Handle image data changes and update parent
  useEffect(() => {
    if (!imageData) {
      setFingerBlob(null);
      setFingerDataURL(null);
      return;
    }

    // console.log("imageData", imageData);
    // console.log("fingerPrint", fingerPrint);
    // Only update parent if this is a new image (not from props)
    if (imageData !== fingerPrint && imageData) {
      try {
        const base64 = imageData.split(",")[1];
        const byteCharacters = atob(base64);
        const byteNumbers = Array.from(byteCharacters, (c) => c.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);

        const file = new File([byteArray], "finger.png", { type: "image/png" });
        const objectURL = URL.createObjectURL(file);
        setFingerDataURL(objectURL);
        setFingerBlob(file);
      } catch (e) {
        console.log("Error in getting fingerprint");
      }
    }
  }, [imageData]); // Removed dependencies that could cause loops

  const initializeSDK = () => {
    const sdkInstance = {
      acquisitionStarted: false,
      sdk: new window.Fingerprint.WebApi(),
      operationToRestart: null,
    };

    sdkInstance.sdk.onDeviceConnected = (e) => {
      setDeviceConnected(true);
      setStatus("Device connected. Ready to capture");
      enumerateReaders();
    };

    sdkInstance.sdk.onDeviceDisconnected = (e) => {
      setDeviceConnected(false);
      setStatus("Device disconnected");
      setCaptureStarted(false);
    };

    sdkInstance.sdk.onCommunicationFailed = (e) => {
      setDeviceConnected(false);
      setStatus("Communication Failed");
      setCaptureStarted(false);
    };

    sdkInstance.sdk.onSamplesAcquired = (s) => {
      sampleAcquired(s);
    };

    sdkInstance.sdk.onQualityReported = (e) => {
      setQuality(window.Fingerprint.QualityCode[e.quality]);
    };

    sdkRef.current = sdkInstance;
    enumerateReaders();
  };

  const enumerateReaders = () => {
    if (!sdkRef.current) return;

    setStatus("Searching for devices...");
    sdkRef.current.sdk.enumerateDevices().then(
      (devices) => {
        setReaders(devices);
        if (devices.length > 0) {
          setSelectedReader(devices[0]);
          getDeviceInfo(devices[0]);
          setStatus("Device connected. Ready to capture");
        } else {
          setStatus("No devices found");
        }
      },
      (error) => {
        setStatus("Device enumeration failed");
        console.error("Device enumeration failed:", error);
      }
    );
  };

  const startCapture = (e) => {
    e.preventDefault();

    if (!sdkRef.current || !selectedReader) {
      setStatus("Device not ready");
      return;
    }

    if (imageData) {
      if (!window.confirm("Replace existing fingerprint?")) {
        return;
      }
    }

    setStatus("Scan your finger...");
    sdkRef.current.operationToRestart = startCapture;

    sdkRef.current.sdk
      .startAcquisition(
        window.Fingerprint.SampleFormat[currentFormat],
        selectedReader
      )
      .then(
        () => {
          sdkRef.current.acquisitionStarted = true;
          setCaptureStarted(true);
          setImageData(null);
        },
        (error) => {
          setStatus(error.message);
          setCaptureStarted(false);
        }
      );
  };

  const stopCapture = () => {
    if (!sdkRef.current || !sdkRef.current.acquisitionStarted) return;

    setStatus("Stopping capture...");
    sdkRef.current.sdk.stopAcquisition().then(
      () => {
        sdkRef.current.acquisitionStarted = false;
        setCaptureStarted(false);
        setStatus("Ready to capture");
      },
      (error) => {
        setStatus(error.message);
      }
    );
  };

  const sampleAcquired = (sample) => {
    if (currentFormat === "PngImage") {
      const samples = JSON.parse(sample.samples);
      const imageSrc = `data:image/png;base64,${window.Fingerprint.b64UrlTo64(
        samples[0]
      )}`;
      setImageData(imageSrc);
      setStatus("Fingerprint captured");
      stopCapture();
    } else {
      setStatus(`${currentFormat} sample acquired`);
      stopCapture();
    }
  };

  const clearCapture = () => {
    setImageData(null);
    setStatus(deviceConnected ? "Ready to capture" : "Device disconnected");
  };

  const getDeviceInfo = (deviceId) => {
    if (!sdkRef.current) return;

    sdkRef.current.sdk.getDeviceInfo(deviceId).then(
      (info) => {
        setDeviceInfo({
          id: info.DeviceID,
          uidType: info.eUidType,
          technology: info.eDeviceTech,
          modality: info.eDeviceModality,
        });
      },
      (error) => {
        console.error("Failed to get device info:", error);
      }
    );
  };

  return (
    <div className="fingerprint-container">
      {imageData && (
        <div className="quality-display">
          <h5>
            Scan Quality:{" "}
            <span className="quality-value">{quality || "Good"}</span>
          </h5>
        </div>
      )}

      {!sdkInitialized ? (
        <div className="loading-message">Initializing fingerprint SDK...</div>
      ) : (
        <div className="capture-tab">
          <div className="status">{status}</div>

          {imageData && (
            <div className="image-preview">
              <img src={imageData} alt="Captured fingerprint" />
            </div>
          )}

          <div className="controls">
            <button
              onClick={clearCapture}
              className="btn btn-clear"
              disabled={!imageData}
            >
              Clear
            </button>

            <button
              onClick={startCapture}
              disabled={!deviceConnected || captureStarted}
              className="btn btn-start"
            >
              {imageData ? "Replace Fingerprint" : "Add Fingerprint"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FingerprintCapture;
